import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { Toaster } from '@/components/ui/sonner';

// Auth Pages - Role-based portals
import PortalSelectorPage from '@/pages/PortalSelectorPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import StaffLoginPage from '@/pages/StaffLoginPage';
import StudentLoginPage from '@/pages/StudentLoginPage';
import ParentLoginPage from '@/pages/ParentLoginPage';
import TenantLoginPage from '@/pages/TenantLoginPage';

// Dashboards
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SchoolAdminDashboard from '@/pages/SchoolAdminDashboard';
import TeacherDashboard from '@/pages/TeacherDashboard';
import StudentDashboard from '@/pages/StudentDashboard';
import ParentDashboard from '@/pages/ParentDashboard';
import StaffDashboardPage from '@/pages/StaffDashboardPage';

// Module Pages
import StudentsPage from '@/pages/StudentsPage';
import TeachersPage from '@/pages/TeachersPage';
import ClassesPage from '@/pages/ClassesPage';
import AttendancePage from '@/pages/AttendancePage';
import AttendancePageV2 from '@/pages/AttendancePageV2';
import ExamsPage from '@/pages/ExamsPage';
import ExamManagementPage from '@/pages/ExamManagementPage';
import OnlineExamPage from '@/pages/OnlineExamPage';
import FeesPage from '@/pages/FeesPage';
// Legacy pages removed - using v2 versions
// import LibraryPage from '@/pages/LibraryPage';
// import TransportPage from '@/pages/TransportPage';
import NoticesPage from '@/pages/NoticesPage';
import TransportPageV2 from '@/pages/TransportPageV2';
import DriverTransportPage from '@/pages/DriverTransportPage';
import LibraryPageV2 from '@/pages/LibraryPageV2';
import HostelPageV2 from '@/pages/HostelPageV2';
import GatePassPage from '@/pages/GatePassPage';
import SalarySlipPage from '@/pages/SalarySlipPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import AccountsPage from '@/pages/AccountsPage';
import NotificationsPage from '@/pages/NotificationsPage';
import NotificationSettingsPage from '@/pages/NotificationSettingsPage';
import HRPage from '@/pages/HRPage';
import ReportsPage from '@/pages/ReportsPage';
import FeeReminderSettingsPage from '@/pages/FeeReminderSettingsPage';
import TimetableManagementPage from '@/pages/TimetableManagementPage';
import AcademicYearPage from '@/pages/AcademicYearPage';
import CommunicationPage from '@/pages/CommunicationPage';
import AnalyticsDashboard from '@/pages/AnalyticsDashboard';
import SubscriptionsPage from '@/pages/SubscriptionsPage';
import TicketsPage from '@/pages/TicketsPage';
import ProvidersPage from '@/pages/ProvidersPage';
import InvoicesPage from '@/pages/InvoicesPage';
import SuperAdminBillingPage from '@/pages/SuperAdminBillingPage';
import BroadcastPage from '@/pages/BroadcastPage';
import HostelPage from '@/pages/HostelPage';
import HomeworkPage from '@/pages/HomeworkPage';
import CertificatesPage from '@/pages/CertificatesPage';
import AlumniPage from '@/pages/AlumniPage';
import MySubscriptionPage from '@/pages/MySubscriptionPage';
import AdmissionFormPage from '@/pages/AdmissionFormPage';
import TeacherFormPage from '@/pages/TeacherFormPage';
import StaffPage from '@/pages/StaffPage';
import StaffFormPage from '@/pages/StaffFormPage';
import FeeManagementPage from '@/pages/FeeManagementPage';
import IdCardsPage from '@/pages/IdCardsPage';
import IdCardBuilderPage from '@/pages/IdCardBuilderPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import UserPasswordResetPage from '@/pages/UserPasswordResetPage';
import UserCredentialsPage from '@/pages/UserCredentialsPage';
import AcademicCalendarPage from '@/pages/AcademicCalendarPage';
import PromotionPage from '@/pages/PromotionPage';
import SchoolStorePage from '@/pages/SchoolStorePage';
import BehaviorTrackingPage from '@/pages/BehaviorTrackingPage';
import AnnouncementsPage from '@/pages/AnnouncementsPage';
import StaffRolesPage from '@/pages/StaffRolesPage';
import GPSTrackingPage from '@/pages/GPSTrackingPage';
import DriverTrackingApp from '@/pages/DriverTrackingApp';
import BiometricPage from '@/pages/BiometricPage';
import StudentMobileApp from '@/pages/StudentMobileApp';
import TeacherMobileApp from '@/pages/TeacherMobileApp';
import PWAManagementPage from '@/pages/PWAManagementPage';
import AppInstallPage from '@/pages/AppInstallPage';
import StudentLedgerPage from '@/pages/StudentLedgerPage';
import SchoolSettingsPage from '@/pages/SchoolSettingsPage';
import VerifyIdCardPage from '@/pages/VerifyIdCardPage';
import IdCardDesignerPage from '@/pages/IdCardDesignerPage';
import AccountingPage from '@/pages/AccountingPage';
import InventoryPage from '@/pages/InventoryPage';
import SuppliersPage from '@/pages/SuppliersPage';

// Legal & Policy Pages
import LegalPage from '@/pages/LegalPage';
import StaticLegalPage from '@/pages/StaticLegalPage';
import LegalPagesManagement from '@/pages/LegalPagesManagement';

// Parent specific pages
import ParentAttendancePage from '@/pages/ParentAttendancePage';
import ParentResultsPage from '@/pages/ParentResultsPage';
import ParentBehaviorPage from '@/pages/ParentBehaviorPage';
import ChildProfilePage from '@/pages/ChildProfilePage';

// Staff/Teacher self-service pages
import StaffProfilePage from '@/pages/StaffProfilePage';
import StaffMyAttendancePage from '@/pages/StaffMyAttendancePage';
import StaffMySalaryPage from '@/pages/StaffMySalaryPage';
import StaffLeavePage from '@/pages/StaffLeavePage';
import StaffAttendanceQRPage from '@/pages/StaffAttendanceQRPage';
import LeaveConfigPage from '@/pages/LeaveConfigPage';

// Student & Parent Profile pages
import StudentProfilePage from '@/pages/StudentProfilePage';
import ParentProfilePage from '@/pages/ParentProfilePage';
import AdminProfilePage from '@/pages/AdminProfilePage';

import ProtectedRoute from '@/components/ProtectedRoute';
import '@/App.css';

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="school-theme">
      <AuthProvider>
        <SessionProvider>
        <BrowserRouter>
          <Routes>
            {/* Public ID Card Verification */}
            <Route path="/verify/:code" element={<VerifyIdCardPage />} />
            
            {/* Legal Pages - Public Routes */}
            <Route path="/page/:pageType" element={<StaticLegalPage />} />
            <Route path="/legal/:schoolCode/:pageType" element={<LegalPage />} />
            
            {/* Auth Routes - Role-based Portals */}
            <Route path="/auth" element={<PortalSelectorPage />} />
            <Route path="/auth/admin" element={<AdminLoginPage />} />
            <Route path="/auth/staff" element={<StaffLoginPage />} />
            <Route path="/auth/student" element={<StudentLoginPage />} />
            <Route path="/auth/parent" element={<ParentLoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            
            {/* Driver Tracking App - Standalone PWA */}
            <Route path="/driver-app" element={<DriverTrackingApp />} />
            
            {/* Driver Dashboard - After login */}
            <Route path="/driver" element={
              <ProtectedRoute allowedRoles={['driver']}>
                <DriverTrackingApp />
              </ProtectedRoute>
            } />
            
            {/* Student Mobile App - Standalone PWA */}
            <Route path="/student-app" element={<StudentMobileApp />} />
            
            {/* Teacher/Staff Mobile App - Standalone PWA */}
            <Route path="/teacher-app" element={<TeacherMobileApp />} />
            
            {/* App Install Page - For QR code scanning */}
            <Route path="/install/:schoolId/:appType" element={<AppInstallPage />} />
            
            {/* Dashboard Routes */}
            <Route path="/super-admin" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/super-admin/subscriptions" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SubscriptionsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/super-admin/tickets" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <TicketsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/super-admin/providers" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <ProvidersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/super-admin/invoices" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminBillingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/super-admin/broadcast" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <BroadcastPage />
              </ProtectedRoute>
            } />
            
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <SchoolAdminDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/teacher" element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <TeacherDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/student" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/parent" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentDashboard />
              </ProtectedRoute>
            } />
            
            {/* Staff Dashboard Route */}
            <Route path="/staff-dashboard" element={
              <ProtectedRoute allowedRoles={['staff']}>
                <StaffDashboardPage />
              </ProtectedRoute>
            } />
            
            {/* Parent Specific Routes */}
            <Route path="/parent/attendance" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentAttendancePage />
              </ProtectedRoute>
            } />
            
            <Route path="/parent/results" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentResultsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/parent/behavior" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentBehaviorPage />
              </ProtectedRoute>
            } />
            
            <Route path="/parent/child/:childId" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ChildProfilePage />
              </ProtectedRoute>
            } />
            
            {/* Admin Profile Route */}
            <Route path="/admin/profile" element={
              <ProtectedRoute allowedRoles={['school_admin', 'super_admin']}>
                <AdminProfilePage />
              </ProtectedRoute>
            } />
            
            {/* Staff/Teacher Self-Service Routes */}
            <Route path="/staff/profile" element={
              <ProtectedRoute allowedRoles={['teacher', 'staff']}>
                <StaffProfilePage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/my-attendance" element={
              <ProtectedRoute allowedRoles={['teacher', 'staff']}>
                <StaffMyAttendancePage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/my-salary" element={
              <ProtectedRoute allowedRoles={['teacher', 'staff']}>
                <StaffMySalaryPage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/leave" element={
              <ProtectedRoute allowedRoles={['teacher', 'staff']}>
                <StaffLeavePage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff-qr-attendance" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <StaffAttendanceQRPage />
              </ProtectedRoute>
            } />
            
            <Route path="/leave-config" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <LeaveConfigPage />
              </ProtectedRoute>
            } />
            
            {/* Student Profile Route */}
            <Route path="/student/profile" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentProfilePage />
              </ProtectedRoute>
            } />
            
            {/* Parent Profile Route */}
            <Route path="/parent/profile" element={
              <ProtectedRoute allowedRoles={['parent']}>
                <ParentProfilePage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff-roles" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <StaffRolesPage />
              </ProtectedRoute>
            } />
            
            {/* Module Routes */}
            <Route path="/tickets" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <TicketsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/students" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'staff']}>
                <StudentsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/admission/new" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <AdmissionFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/admission/edit/:studentId" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <AdmissionFormPage editMode={true} />
              </ProtectedRoute>
            } />
            
            <Route path="/teachers" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <TeachersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/teachers/new" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <TeacherFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/teachers/edit/:id" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <TeacherFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <StaffPage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/new" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <StaffFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/staff/edit/:id" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <StaffFormPage />
              </ProtectedRoute>
            } />
            
            <Route path="/classes" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'staff']}>
                <ClassesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/attendance" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'staff']}>
                <AttendancePageV2 />
              </ProtectedRoute>
            } />
            
            <Route path="/exams" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'staff']}>
                <ExamManagementPage />
              </ProtectedRoute>
            } />
            
            <Route path="/online-exam" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'staff']}>
                <OnlineExamPage />
              </ProtectedRoute>
            } />
            
            <Route path="/fee-management" element={
              <ProtectedRoute allowedRoles={['school_admin', 'student', 'parent', 'staff']}>
                <FeeManagementPage />
              </ProtectedRoute>
            } />
            
            <Route path="/student-ledger/:studentId" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff', 'parent']}>
                <StudentLedgerPage />
              </ProtectedRoute>
            } />
            
            <Route path="/id-cards" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <IdCardsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/id-card-builder" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <IdCardDesignerPage />
              </ProtectedRoute>
            } />
            
            <Route path="/password-reset" element={
              <ProtectedRoute allowedRoles={['super_admin', 'school_admin']}>
                <UserPasswordResetPage />
              </ProtectedRoute>
            } />
            
            <Route path="/user-credentials" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <UserCredentialsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/academic-calendar" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'staff', 'student', 'parent']}>
                <AcademicCalendarPage />
              </ProtectedRoute>
            } />
            
            <Route path="/promotion" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <PromotionPage />
              </ProtectedRoute>
            } />
            
            <Route path="/store" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'staff']}>
                <SchoolStorePage />
              </ProtectedRoute>
            } />
            
            <Route path="/accounting" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <AccountingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/inventory" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <InventoryPage />
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <SuppliersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/behavior" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'staff']}>
                <BehaviorTrackingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/announcements" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'parent', 'staff']}>
                <AnnouncementsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/library" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'staff']}>
                <LibraryPageV2 />
              </ProtectedRoute>
            } />
            
            <Route path="/notices" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'parent', 'staff']}>
                <NoticesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/fees" element={
              <ProtectedRoute allowedRoles={['student', 'parent']}>
                <FeesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/transport" element={
              <ProtectedRoute allowedRoles={['school_admin', 'student', 'parent', 'staff']}>
                <TransportPageV2 />
              </ProtectedRoute>
            } />
            
            <Route path="/gps-tracking" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'parent', 'staff']}>
                <GPSTrackingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/biometric" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <BiometricPage />
              </ProtectedRoute>
            } />
            
            <Route path="/app-management" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <PWAManagementPage />
              </ProtectedRoute>
            } />
            
            <Route path="/school-settings" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <SchoolSettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/integrations" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <IntegrationsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/accounts" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <AccountsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/notifications" element={
              <ProtectedRoute allowedRoles={['super_admin', 'school_admin', 'teacher', 'student', 'parent', 'staff']}>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/notification-settings" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <NotificationSettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/hr" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <HRPage />
              </ProtectedRoute>
            } />
            
            <Route path="/salary" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <SalarySlipPage />
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <ReportsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/fee-reminders" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <FeeReminderSettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/legal-pages" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <LegalPagesManagement />
              </ProtectedRoute>
            } />
            
            <Route path="/timetable" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'staff']}>
                <TimetableManagementPage />
              </ProtectedRoute>
            } />
            
            <Route path="/academic" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <AcademicYearPage />
              </ProtectedRoute>
            } />
            
            <Route path="/communication" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'parent', 'staff']}>
                <CommunicationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/analytics" element={
              <ProtectedRoute allowedRoles={['school_admin', 'super_admin', 'staff']}>
                <AnalyticsDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/hostel" element={
              <ProtectedRoute allowedRoles={['school_admin', 'student', 'staff']}>
                <HostelPageV2 />
              </ProtectedRoute>
            } />
            
            <Route path="/gate-pass" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'parent', 'staff']}>
                <GatePassPage />
              </ProtectedRoute>
            } />
            
            <Route path="/homework" element={
              <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'student', 'staff']}>
                <HomeworkPage />
              </ProtectedRoute>
            } />
            
            <Route path="/certificates" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <CertificatesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/alumni" element={
              <ProtectedRoute allowedRoles={['school_admin', 'staff']}>
                <AlumniPage />
              </ProtectedRoute>
            } />
            
            <Route path="/my-subscription" element={
              <ProtectedRoute allowedRoles={['school_admin']}>
                <MySubscriptionPage />
              </ProtectedRoute>
            } />
            
            {/* Tenant Login Route - must be LAST before default redirect */}
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/auth" replace />} />
            
            {/* Tenant Login - MUST be last as it catches all unknown paths */}
            <Route path="/:schoolCode" element={<TenantLoginPage />} />
          </Routes>
        </BrowserRouter>
        </SessionProvider>
        <Toaster position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
